import BaseService from './BaseService.js';
import { addDoc, collection, doc, setDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';

/**
 * User Service for handling all user-related Firebase operations
 * Extends BaseService to provide user-specific functionality with caching
 */
class UserService extends BaseService {
  constructor() {
    super('users');
    this.cacheTimeout = 10 * 60 * 1000; // 10 minutes for users (less frequent updates)
  }

  /**
   * Get all users (admin only)
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Array>} Array of users
   */
  async getAllUsers(useCache = true) {
    try {
      // Sort users by displayName (or username if displayName is not available)
      const users = await this.getDocuments({}, { field: 'displayName', direction: 'asc' }, null, useCache);
      return users.map(user => this.addComputedProperties(user));
    } catch (error) {
      console.error('Error fetching all users:', error);
      throw error;
    }
  }

  /**
   * Get a single user by ID with caching
   * @param {string} uid - User ID
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object|null>} User object or null
   */
  async getUser(uid, useCache = true) {
    if (!uid) {
      throw new Error('User ID is required');
    }

    try {
      console.log('UserService: Fetching user with uid:', uid);
      const user = await this.getDocument(uid, useCache);
      console.log('UserService: Retrieved user from Firestore:', user);
      return user ? this.addComputedProperties(user) : null;
    } catch (error) {
      console.error('Error fetching user:', error);
      
      // Check if it's a permission error during signup
      if (error.code === 'permission-denied' || error.message?.includes('permission')) {
        console.log('Permission denied during user fetch, likely during signup process');
        return null; // Return null instead of throwing for permission errors during signup
      }
      
      // Try to handle auth error and retry once
      const tokenRefreshed = await this.handleAuthError(error, 'getUser');
      if (tokenRefreshed) {
        try {
          const user = await this.getDocument(uid, useCache);
          return user ? this.addComputedProperties(user) : null;
        } catch (retryError) {
          console.error('Error fetching user after token refresh:', retryError);
          throw retryError;
        }
      }
      
      throw error;
    }
  }

  /**
   * Get current authenticated user
   * @returns {Promise<Object|null>} Current user object or null
   */
  async getCurrentUser() {
    try {
      // Import Firebase auth dynamically to avoid circular dependencies
      const { getAuth, onAuthStateChanged } = await import('firebase/auth');
      const auth = getAuth();
      
      return new Promise((resolve) => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
          unsubscribe();
          resolve(user);
        });
      });
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  }

  /**
   * Get user by email
   * @param {string} email - User email
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object|null>} User object or null
   */
  async getUserByEmail(email, useCache = true) {
    if (!email) {
      throw new Error('Email is required');
    }

    try {
      const users = await this.getDocuments({ username: email }, null, null, useCache);
      return users.length > 0 ? this.addComputedProperties(users[0]) : null;
    } catch (error) {
      console.error('Error fetching user by email:', error);
      
      // Try to handle auth error and retry once
      const tokenRefreshed = await this.handleAuthError(error, 'getUserByEmail');
      if (tokenRefreshed) {
        try {
          const users = await this.getDocuments({ username: email }, null, null, useCache);
          return users.length > 0 ? this.addComputedProperties(users[0]) : null;
        } catch (retryError) {
          console.error('Error fetching user by email after token refresh:', retryError);
          throw retryError;
        }
      }
      
      throw error;
    }
  }

  /**
   * Create a new user
   * @param {Object} userData - User data
   * @returns {Promise<Object>} Created user
   */
  async createUser(userData) {
    if (!userData.uid) {
      throw new Error('User ID is required');
    }

    if (!userData.username) {
      throw new Error('Username/email is required');
    }

    try {
      console.log('UserService: Creating user with data:', userData);
      
      // Prepare user data with defaults
      const newUserData = {
        ...userData,
        demoUser: userData.demoUser || false,
        displayName: userData.displayName || userData.username,
        emailUnsubscribed: userData.emailUnsubscribed || false,
        isAdmin: userData.isAdmin || false,
        testUser: userData.testUser || false
        // Removed trialData as requested earlier
      };

      console.log('UserService: Prepared user data:', newUserData);
      const createdUser = await this.createUserDocument(newUserData);
      console.log('UserService: User created successfully:', createdUser);
      return this.addComputedProperties(createdUser);
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  }

  /**
   * Create a user document with correct field names
   * @param {Object} data - User data
   * @returns {Promise<Object>} Created user
   */
  async createUserDocument(data) {
    try {
      // Use the user's UID as the document ID
      const docRef = doc(db, this.collectionName, data.uid);
      await setDoc(docRef, {
        ...data,
        created: Date.now() // Use 'created' field for consistency
      });

      // Clear relevant cache
      this.clearCache();

      return {
        key: data.uid,
        ...data,
        created: Date.now()
      };
    } catch (error) {
      console.error(`Error creating user document:`, error);
      throw error;
    }
  }

  /**
   * Update an existing user
   * @param {string} uid - User ID
   * @param {Object} updateData - Data to update
   * @returns {Promise<Object>} Updated user
   */
  async updateUser(uid, updateData) {
    if (!uid) {
      throw new Error('User ID is required');
    }

    try {
      // If updating email, check for duplicates
      if (updateData.username) {
        const currentUser = await this.getUser(uid, false);
        if (currentUser) {
          const existingUser = await this.getUserByEmail(updateData.username, false);
          if (existingUser && existingUser.key !== uid) {
            throw new Error(`A user with the email '${updateData.username}' already exists`);
          }
        }
      }

      const updatedUser = await this.updateDocument(uid, updateData);
      return this.addComputedProperties(updatedUser);
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  /**
   * Update user's email verification status
   * @param {string} uid - User ID
   * @param {boolean} emailVerified - Email verification status
   * @returns {Promise<Object>} Updated user
   */
  async updateEmailVerificationStatus(uid, emailVerified) {
    if (!uid) {
      throw new Error('User ID is required');
    }

    try {
      // First, try to get the existing user document
      const existingUser = await this.getUser(uid, false);
      
      if (!existingUser) {
        console.warn('User document not found for email verification update, skipping...');
        return null;
      }

      const updateData = {
        emailVerified: emailVerified,
        emailVerifiedAt: emailVerified ? Date.now() : null
      };

      // Try to handle auth error and retry once
      try {
        const updatedUser = await this.updateDocument(uid, updateData);
        return this.addComputedProperties(updatedUser);
      } catch (error) {
        const tokenRefreshed = await this.handleAuthError(error, 'updateEmailVerificationStatus');
        if (tokenRefreshed) {
          try {
            const updatedUser = await this.updateDocument(uid, updateData);
            return this.addComputedProperties(updatedUser);
          } catch (retryError) {
            console.error('Error updating email verification status after token refresh:', retryError);
            throw retryError;
          }
        }
        throw error;
      }
    } catch (error) {
      console.error('Error updating email verification status:', error);
      // Don't throw error here, as this is not critical for the main flow
      return null;
    }
  }

  /**
   * Delete a user
   * @param {string} uid - User ID
   * @param {string} email - User email (optional, for logging)
   * @param {string} username - Username (optional, for logging)
   * @returns {Promise<boolean>} Success status
   */
  async deleteUser(uid, email = null, username = null) {
    if (!uid) {
      throw new Error('User ID is required');
    }

    try {
      console.log("Deleting user " + (username || 'unknown') + " " + (email || 'unknown') + " " + uid);
      
      // First, delete user files from Cloudflare
      try {
        const cloudflareService = (await import('./CloudflareService.js')).default;
        await cloudflareService.deleteUserFiles(uid);
        console.log('Successfully deleted user files from Cloudflare');
      } catch (cloudflareError) {
        console.warn('Failed to delete user files from Cloudflare:', cloudflareError);
        // Continue with user deletion even if file deletion fails
      }

      // Call Firebase function to delete user (this should handle Firestore deletion server-side)
      try {
        const { getFunctions, httpsCallable } = await import('firebase/functions');
        const functions = getFunctions();
        const result = await httpsCallable(functions, 'deleteUser')({
          uid: uid, 
          email: email, 
          username: username
        });
        console.log('Successfully called Firebase deleteUser function:', result);
        
        // If Firebase function succeeds, we don't need to delete locally
        // The server-side function should handle all deletion
        return true;
      } catch (firebaseError) {
        console.error('Failed to call Firebase deleteUser function:', firebaseError);
        
        // Only attempt local deletion if Firebase function fails and user has admin privileges
        try {
          const currentUser = await this.getCurrentUser();
          if (currentUser) {
            const currentUserData = await this.getUser(currentUser.uid, false);
            if (currentUserData?.isAdmin) {
              console.log('Attempting local deletion as admin user...');
              await this.deleteDocument(uid);
              console.log('Successfully deleted user document from Firestore (admin override)');
              return true;
            } else {
              throw new Error('Insufficient permissions: Only admin users can delete users when Firebase function fails');
            }
          } else {
            throw new Error('No authenticated user found for deletion');
          }
        } catch (localDeleteError) {
          console.error('Failed to delete user document locally:', localDeleteError);
          throw new Error(`User deletion failed: ${firebaseError.message}. Local deletion also failed: ${localDeleteError.message}`);
        }
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  }

  /**
   * Get admin users
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Array>} Array of admin users
   */
  async getAdminUsers(useCache = true) {
    try {
      const users = await this.getDocuments({ isAdmin: true }, { field: 'displayName', direction: 'asc' }, null, useCache);
      return users.map(user => this.addComputedProperties(user));
    } catch (error) {
      console.error('Error fetching admin users:', error);
      throw error;
    }
  }

  /**
   * Get demo users
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Array>} Array of demo users
   */
  async getDemoUsers(useCache = true) {
    try {
      const users = await this.getDocuments({ demoUser: true }, { field: 'displayName', direction: 'asc' }, null, useCache);
      return users.map(user => this.addComputedProperties(user));
    } catch (error) {
      console.error('Error fetching demo users:', error);
      throw error;
    }
  }

  /**
   * Get test users
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Array>} Array of test users
   */
  async getTestUsers(useCache = true) {
    try {
      const users = await this.getDocuments({ testUser: true }, { field: 'displayName', direction: 'asc' }, null, useCache);
      return users.map(user => this.addComputedProperties(user));
    } catch (error) {
      console.error('Error fetching test users:', error);
      throw error;
    }
  }

  /**
   * Get users with expired trials
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Array>} Array of users with expired trials
   */
  async getExpiredTrialUsers(useCache = true) {
    try {
      const users = await this.getAllUsers(useCache);
      return users.filter(user => user.trialExpired);
    } catch (error) {
      console.error('Error fetching expired trial users:', error);
      throw error;
    }
  }

  /**
   * Get users with active trials
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Array>} Array of users with active trials
   */
  async getActiveTrialUsers(useCache = true) {
    try {
      const users = await this.getAllUsers(useCache);
      return users.filter(user => user.trialActive);
    } catch (error) {
      console.error('Error fetching active trial users:', error);
      throw error;
    }
  }

  /**
   * Update user trial data
   * @param {string} uid - User ID
   * @param {Object} trialData - Trial data to update
   * @returns {Promise<Object>} Updated user
   */
  async updateTrialData(uid, trialData) {
    if (!uid) {
      throw new Error('User ID is required');
    }

    try {
      const updatedUser = await this.updateDocument(uid, { trialData });
      return this.addComputedProperties(updatedUser);
    } catch (error) {
      console.error('Error updating trial data:', error);
      throw error;
    }
  }

  /**
   * Extend user trial
   * @param {string} uid - User ID
   * @param {number} days - Number of days to extend
   * @returns {Promise<Object>} Updated user
   */
  async extendTrial(uid, days) {
    if (!uid) {
      throw new Error('User ID is required');
    }

    if (!days || days <= 0) {
      throw new Error('Days must be a positive number');
    }

    try {
      const user = await this.getUser(uid, false);
      if (!user) {
        throw new Error('User not found');
      }

      const currentTrialData = user.trialData || {};
      const newEndDate = currentTrialData.endDate ? 
        Math.max(currentTrialData.endDate, Date.now()) + (days * 24 * 60 * 60 * 1000) :
        Date.now() + (days * 24 * 60 * 60 * 1000);

      const updatedTrialData = {
        ...currentTrialData,
        days: (currentTrialData.days || 0) + days,
        endDate: newEndDate,
        expired: false
      };

      const updatedUser = await this.updateDocument(uid, { trialData: updatedTrialData });
      return this.addComputedProperties(updatedUser);
    } catch (error) {
      console.error('Error extending trial:', error);
      throw error;
    }
  }

  /**
   * Get user statistics
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object>} User statistics
   */
  async getUserStats(useCache = true) {
    try {
      const users = await this.getAllUsers(useCache);
      
      const stats = {
        total: users.length,
        admins: users.filter(u => u.isAdmin).length,
        demoUsers: users.filter(u => u.demoUser).length,
        testUsers: users.filter(u => u.testUser).length,
        trialExpired: users.filter(u => u.trialExpired).length,
        trialActive: users.filter(u => u.trialActive).length,
        emailUnsubscribed: users.filter(u => u.emailUnsubscribed).length,
        byTrialStatus: {
          active: users.filter(u => u.trialActive).length,
          expired: users.filter(u => u.trialExpired).length,
          noTrial: users.filter(u => !u.trialData).length
        }
      };

      return stats;
    } catch (error) {
      console.error('Error getting user stats:', error);
      throw error;
    }
  }

  /**
   * Add computed properties to user object
   * @param {Object} user - User object
   * @returns {Object} User with computed properties
   */
  addComputedProperties(user) {
    if (!user) return null;

    const now = Date.now();
    const trialData = user.trialData || {};
    
    return {
      ...user,
      // Trial status
      trialActive: trialData.endDate && trialData.endDate > now && !trialData.expired,
      trialExpired: trialData.endDate && (trialData.endDate <= now || trialData.expired),
      trialDaysRemaining: trialData.endDate && trialData.endDate > now ? 
        Math.ceil((trialData.endDate - now) / (24 * 60 * 60 * 1000)) : 0,
      // Email status
      emailSubscribed: !user.emailUnsubscribed,
      // Trial information
      trialEndDate: trialData.endDate ? new Date(trialData.endDate).toLocaleDateString() : null,
      trialEndDateFormatted: trialData.endDate ? new Date(trialData.endDate).toLocaleString() : null,
      // User info summary
      displayNameOrEmail: user.displayName || user.username || 'Unknown User',
      userType: user.isAdmin ? 'Admin' : user.demoUser ? 'Demo' : user.testUser ? 'Test' : 'Regular',
      // Status indicators - check email verification first, then trial status
      status: !user.emailVerified ? 'NoVer' : user.trialExpired ? 'Trial Expired' : user.trialActive ? 'Trial Active' : 'Active',
      statusColor: !user.emailVerified ? 'red' : user.trialExpired ? 'red' : user.trialActive ? 'yellow' : 'green'
    };
  }

  /**
   * Set up real-time listener for all users (admin only)
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  subscribeToAllUsers(callback) {
    try {
      return this.subscribeToDocuments(
        {},
        (users, changes) => {
          const usersWithComputed = users.map(user => this.addComputedProperties(user));
          callback(usersWithComputed, changes);
        },
        { sortBy: { field: 'displayName', direction: 'asc' } }
      );
    } catch (indexError) {
      // If index doesn't exist, fall back to query without sorting
      console.warn('Composite index not found for users listener, falling back to unsorted query.');
      
      return this.subscribeToDocuments(
        {},
        (users, changes) => {
          const usersWithComputed = users.map(user => this.addComputedProperties(user));
          callback(usersWithComputed, changes);
        },
        {} // No sorting options
      );
    }
  }

  /**
   * Set up real-time listener for a specific user
   * @param {string} uid - User ID
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  subscribeToUser(uid, callback) {
    if (!uid) {
      throw new Error('User ID is required');
    }

    return this.subscribeToDocument(uid, (user, change) => {
      const userWithComputed = user ? this.addComputedProperties(user) : null;
      callback(userWithComputed, change);
    });
  }

  /**
   * Batch update multiple users
   * @param {Array} operations - Array of update operations
   * @returns {Promise<boolean>} Success status
   */
  async batchUpdateUsers(operations) {
    try {
      const batchOperations = operations.map(op => ({
        type: 'update',
        id: op.userId,
        data: op.updateData
      }));

      await this.batchOperation(batchOperations);
      return true;
    } catch (error) {
      console.error('Error in batch update users:', error);
      throw error;
    }
  }

  /**
   * Delete a user with admin privileges (bypasses Firebase function)
   * @param {string} uid - User ID
   * @param {string} email - User email (optional, for logging)
   * @param {string} username - Username (optional, for logging)
   * @returns {Promise<boolean>} Success status
   */
  async deleteUserAsAdmin(uid, email = null, username = null) {
    if (!uid) {
      throw new Error('User ID is required');
    }

    try {
      console.log("Admin deleting user " + (username || 'unknown') + " " + (email || 'unknown') + " " + uid);
      
      // Verify current user has admin privileges
      const currentUser = await this.getCurrentUser();
      if (!currentUser) {
        throw new Error('No authenticated user found');
      }
      
      const currentUserData = await this.getUser(currentUser.uid, false);
      if (!currentUserData?.isAdmin) {
        throw new Error('Insufficient permissions: Admin privileges required');
      }

      // Delete user files from Cloudflare
      try {
        const cloudflareService = (await import('./CloudflareService.js')).default;
        await cloudflareService.deleteUserFiles(uid);
        console.log('Successfully deleted user files from Cloudflare');
      } catch (cloudflareError) {
        console.warn('Failed to delete user files from Cloudflare:', cloudflareError);
      }

      // Delete user document from Firestore (admin override)
      await this.deleteDocument(uid);
      console.log('Successfully deleted user document from Firestore (admin override)');
      
      return true;
    } catch (error) {
      console.error('Error deleting user as admin:', error);
      throw error;
    }
  }

  /**
   * Get information about required Firestore indexes
   * @returns {Object} Index information for manual creation
   */
  getRequiredIndexes() {
    return {
      users: {
        collection: 'users',
        indexes: [
          {
            name: 'created_desc',
            fields: [
              { fieldPath: 'created', order: 'DESCENDING' }
            ],
            description: 'For efficient querying of users sorted by creation date'
          },
          {
            name: 'username',
            fields: [
              { fieldPath: 'username', order: 'ASCENDING' }
            ],
            description: 'For efficient querying of users by email/username'
          },
          {
            name: 'isAdmin',
            fields: [
              { fieldPath: 'isAdmin', order: 'ASCENDING' }
            ],
            description: 'For efficient querying of admin users'
          },
          {
            name: 'demoUser',
            fields: [
              { fieldPath: 'demoUser', order: 'ASCENDING' }
            ],
            description: 'For efficient querying of demo users'
          },
          {
            name: 'testUser',
            fields: [
              { fieldPath: 'testUser', order: 'ASCENDING' }
            ],
            description: 'For efficient querying of test users'
          }
        ]
      }
    };
  }

  /**
   * Log index creation instructions to console
   */
  logIndexInstructions() {
    const indexes = this.getRequiredIndexes();
    console.group('🔧 Firestore Index Creation Instructions for Users');
    console.log('To improve performance, create the following composite indexes in Firebase Console:');
    
    indexes.users.indexes.forEach(index => {
      console.group(`📋 ${index.name}`);
      console.log(`Collection: ${indexes.users.collection}`);
      console.log(`Description: ${index.description}`);
      console.log('Fields:');
      index.fields.forEach(field => {
        console.log(`  - ${field.fieldPath}: ${field.order}`);
      });
      console.groupEnd();
    });
    
    console.log('\n📝 Steps to create indexes:');
    console.log('1. Go to Firebase Console > Firestore Database > Indexes');
    console.log('2. Click "Add Index"');
    console.log('3. Select collection: "users"');
    console.log('4. Add fields as shown above');
    console.log('5. Click "Create Index"');
    console.log('6. Wait for index to build (may take a few minutes)');
    console.groupEnd();
  }

  /**
   * Clean up orphaned user data (when Firebase Auth user is deleted but Firestore data remains)
   * @param {string} email - User email to check
   * @returns {Promise<boolean>} Success status
   */
  async cleanupOrphanedUser(email) {
    if (!email) {
      throw new Error('Email is required');
    }

    try {
      console.log('Checking for orphaned user data for email:', email);
      
      // Find user by email in Firestore
      const user = await this.getUserByEmail(email, false);
      if (!user) {
        console.log('No user found in Firestore for email:', email);
        return false;
      }

      // Check if Firebase Auth user still exists
      try {
        const { getAuth, fetchSignInMethodsForEmail } = await import('firebase/auth');
        const auth = getAuth();
        const signInMethods = await fetchSignInMethodsForEmail(auth, email);
        
        if (signInMethods.length === 0) {
          console.log('Firebase Auth user not found, cleaning up orphaned Firestore data for:', email);
          
          // Delete the orphaned user data
          await this.deleteUserAsAdmin(user.key, email, user.displayName || user.username);
          console.log('Successfully cleaned up orphaned user data for:', email);
          return true;
        } else {
          console.log('Firebase Auth user still exists for:', email);
          return false;
        }
      } catch (authError) {
        console.error('Error checking Firebase Auth user:', authError);
        // If we can't check Firebase Auth, assume user is orphaned and clean up
        console.log('Assuming user is orphaned due to auth check failure, cleaning up:', email);
        await this.deleteUserAsAdmin(user.key, email, user.displayName || user.username);
        return true;
      }
    } catch (error) {
      console.error('Error cleaning up orphaned user:', error);
      throw error;
    }
  }

  /**
   * Get user's email subscription status
   * @param {string} uid - User ID
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<boolean>} Email subscription status (true = subscribed, false = unsubscribed)
   */
  async getUserEmailSubscription(uid, useCache = true) {
    if (!uid) {
      throw new Error('User ID is required');
    }

    try {
      const user = await this.getUser(uid, useCache);
      if (!user) {
        throw new Error('User not found');
      }
      
      // Return the opposite of emailUnsubscribed (true = subscribed, false = unsubscribed)
      return !user.emailUnsubscribed;
    } catch (error) {
      console.error('Error getting user email subscription status:', error);
      throw error;
    }
  }

  /**
   * Set user's email subscription status
   * @param {string} uid - User ID
   * @param {boolean} subscribed - Email subscription status (true = subscribe, false = unsubscribe)
   * @returns {Promise<Object>} Updated user object
   */
  async setUserEmailSubscription(uid, subscribed) {
    if (!uid) {
      throw new Error('User ID is required');
    }

    if (typeof subscribed !== 'boolean') {
      throw new Error('Subscribed parameter must be a boolean');
    }

    try {
      // Convert subscribed boolean to emailUnsubscribed (opposite logic)
      const emailUnsubscribed = !subscribed;
      
      const updateData = {
        emailUnsubscribed: emailUnsubscribed
      };

      const updatedUser = await this.updateDocument(uid, updateData);
      return this.addComputedProperties(updatedUser);
    } catch (error) {
      console.error('Error setting user email subscription status:', error);
      throw error;
    }
  }

  /**
   * Subscribe user to emails (set emailUnsubscribed to false)
   * @param {string} uid - User ID
   * @returns {Promise<Object>} Updated user object
   */
  async subscribeUserToEmails(uid) {
    return this.setUserEmailSubscription(uid, true);
  }

  /**
   * Unsubscribe user from emails (set emailUnsubscribed to true)
   * @param {string} uid - User ID
   * @returns {Promise<Object>} Updated user object
   */
  async unsubscribeUserFromEmails(uid) {
    return this.setUserEmailSubscription(uid, false);
  }

  /**
   * Get all users with their email subscription status
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Array>} Array of users with email subscription info
   */
  async getUsersWithEmailSubscriptionStatus(useCache = true) {
    try {
      const users = await this.getAllUsers(useCache);
      return users.map(user => ({
        ...user,
        emailSubscribed: !user.emailUnsubscribed,
        emailSubscriptionStatus: user.emailUnsubscribed ? 'unsubscribed' : 'subscribed'
      }));
    } catch (error) {
      console.error('Error getting users with email subscription status:', error);
      throw error;
    }
  }

  /**
   * Get subscribed users (emailUnsubscribed = false)
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Array>} Array of subscribed users
   */
  async getSubscribedUsers(useCache = true) {
    try {
      const users = await this.getAllUsers(useCache);
      return users.filter(user => !user.emailUnsubscribed);
    } catch (error) {
      console.error('Error getting subscribed users:', error);
      throw error;
    }
  }

  /**
   * Get unsubscribed users (emailUnsubscribed = true)
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Array>} Array of unsubscribed users
   */
  async getUnsubscribedUsers(useCache = true) {
    try {
      const users = await this.getAllUsers(useCache);
      return users.filter(user => user.emailUnsubscribed);
    } catch (error) {
      console.error('Error getting unsubscribed users:', error);
      throw error;
    }
  }

  /**
   * Get user subscriptions from Stripe (active and trialing subscriptions)
   * @param {string} uid - User ID
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Array>} Array of user subscriptions
   */
  async getUserSubscriptions(uid, useCache = true) {
    if (!uid) {
      throw new Error('User ID is required');
    }

    try {
      // Import Firebase functions dynamically to avoid circular dependencies
      const { collection, query, where, getDocs } = await import('firebase/firestore');
      
      // Query stripe_customers/{uid}/subscriptions collection for active, trialing, and canceled subscriptions
      const subscriptionsCollection = collection(db, "stripe_customers", uid, "subscriptions");
      const subscriptionsQuery = query(subscriptionsCollection, where('status', 'in', ['active', 'trialing', 'canceled']));
      
      const querySnapshot = await getDocs(subscriptionsQuery);
      const subscriptions = [];
      
      querySnapshot.forEach((doc) => {
        const subscriptionData = doc.data();
        subscriptionData.id = doc.id;
        subscriptions.push(subscriptionData);
      });
      
      return subscriptions;
    } catch (error) {
      console.error('Error fetching user subscriptions:', error);
      
      // Check if it's a permission error
      if (error.code === 'permission-denied' || error.message?.includes('permission')) {
        console.log('Permission denied during user subscriptions fetch');
        return []; // Return empty array for permission errors
      }
      
      // Try to handle auth error and retry once
      const tokenRefreshed = await this.handleAuthError(error, 'getUserSubscriptions');
      if (tokenRefreshed) {
        try {
          const { collection, query, where, getDocs } = await import('firebase/firestore');
          const subscriptionsCollection = collection(db, "stripe_customers", uid, "subscriptions");
          const subscriptionsQuery = query(subscriptionsCollection, where('status', 'in', ['trialing', 'active', 'canceled']));
          
          const querySnapshot = await getDocs(subscriptionsQuery);
          const subscriptions = [];
          
          querySnapshot.forEach((doc) => {
            const subscriptionData = doc.data();
            subscriptionData.id = doc.id;
            subscriptions.push(subscriptionData);
          });
          
          return subscriptions;
        } catch (retryError) {
          console.error('Error fetching user subscriptions after token refresh:', retryError);
          throw retryError;
        }
      }
      
      throw error;
    }
  }

  /**
   * Listen to the user's Stripe subscriptions (active, trialing and canceled).
   * Fires whenever the Stripe webhook updates them, e.g. after a cancel in the
   * Stripe billing portal, so pages stay current without a reload.
   * @param {string} uid - User ID
   * @param {Function} onChange - Called with the array of subscriptions
   * @returns {Promise<Function>} Unsubscribe function
   */
  async listenToUserSubscriptions(uid, onChange) {
    if (!uid) {
      throw new Error('User ID is required');
    }

    const { collection, query, where, onSnapshot } = await import('firebase/firestore');
    const subscriptionsQuery = query(
      collection(db, "stripe_customers", uid, "subscriptions"),
      where('status', 'in', ['active', 'trialing', 'canceled'])
    );

    return onSnapshot(subscriptionsQuery, (querySnapshot) => {
      const subscriptions = querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
      onChange(subscriptions);
    }, (error) => {
      console.error('Error listening to user subscriptions:', error);
    });
  }

  /**
   * Debug method to check raw data in the users collection
   * @param {string} uid - User ID (optional, if not provided checks all users)
   * @returns {Promise<Array>} Raw users data
   */
  async debugGetAllUsers(uid = null) {
    console.log('🔍 Debug: Getting all users from collection...');
    
    try {
      let users;
      if (uid) {
        console.log('🔍 Debug: Getting specific user:', uid);
        const user = await this.getDocument(uid, false);
        users = user ? [user] : [];
      } else {
        console.log('🔍 Debug: Getting all users without filter');
        users = await this.getDocuments({}, null, null, false);
      }
      
      console.log('🔍 Debug: Raw users found:', users);
      console.log('🔍 Debug: Users count:', users.length);
      
      if (users.length > 0) {
        console.log('🔍 Debug: Sample user structure:', users[0]);
      }
      
      return users;
    } catch (error) {
      console.error('🔍 Debug: Error getting all users:', error);
      throw error;
    }
  }

  /**
   * Set device override for a user
   * @param {string} userId - User ID
   * @param {number} deviceCount - Number of devices to allow
   * @returns {Promise<boolean>} Success status
   */
  async setDeviceOverride(userId, deviceCount) {
    try {
      await this.updateUser(userId, {
        deviceOverride: deviceCount
      });
      
      console.log(`✅ Device override set to ${deviceCount} for user ${userId}`);
      return true;
    } catch (error) {
      console.error('❌ Error setting device override:', error);
      throw error;
    }
  }

  /**
   * Remove device override for a user
   * @param {string} userId - User ID
   * @returns {Promise<boolean>} Success status
   */
  async removeDeviceOverride(userId) {
    try {
      await this.updateUser(userId, {
        deviceOverride: null
      });
      
      console.log(`✅ Device override removed for user ${userId}`);
      return true;
    } catch (error) {
      console.error('❌ Error removing device override:', error);
      throw error;
    }
  }
}

// Create singleton instance
const userService = new UserService();

/**
 * Static helper: Returns true if the user is an admin.
 * @param {Object} user
 * @returns {boolean}
 */
userService.isUserAdmin = function(user) {
  return !!user && user.isAdmin === true;
};

/**
 * Static helper: Returns true if the user is a test user.
 * @param {Object} user
 * @returns {boolean}
 */
userService.isUserTest = function(user) {
  return !!user && user.testUser === true;
};

/**
 * Static helper: Returns true if the user is a demo user.
 * @param {Object} user
 * @returns {boolean}
 */
userService.isUserDemo = function(user) {
  return !!user && user.demoUser === true;
};

/**
 * Static helper: Returns true if the user is a regular user (not admin, test, or demo).
 * @param {Object} user
 * @returns {boolean}
 */
userService.isUserRegular = function(user) {
  return !!user && 
         (user.isAdmin !== true) && 
         (user.testUser !== true) && 
         (user.demoUser !== true);
};

export default userService; 